import * as Schema from "effect/Schema";
import { TrimmedNonEmptyString } from "./baseSchemas.js";
import { EventId, IsoDateTime, ProviderItemId, ThreadId, TurnId } from "./baseSchemas.js";
import { ChatAttachment, ModelSelection, PROVIDER_SEND_TURN_MAX_ATTACHMENTS, PROVIDER_SEND_TURN_MAX_INPUT_CHARS, } from "./orchestration.js";
import { ProviderInstanceId, ProviderDriverKind } from "./providerInstance.js";
const ProviderSessionStatus = Schema.Literals([
    "connecting",
    "ready",
    "running",
    "error",
    "closed",
]);
export const ProviderSession = Schema.Struct({
    provider: ProviderDriverKind,
    // Optional during the driver/instance migration. Once every producer
    // populates it (post-slice-4), routing flips to instance-id-only and the
    // legacy `provider` field is removed.
    providerInstanceId: Schema.optional(ProviderInstanceId),
    status: ProviderSessionStatus,
    cwd: Schema.optional(TrimmedNonEmptyString),
    model: Schema.optional(TrimmedNonEmptyString),
    threadId: ThreadId,
    resumeCursor: Schema.optional(Schema.Unknown),
    activeTurnId: Schema.optional(TurnId),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    lastError: Schema.optional(TrimmedNonEmptyString),
    /**
     * MCP servers this session declared and failed to start.
     *
     * Carried on the session because recovery is a session-level act: the CLI
     * attempts each server once per process and offers no way to restart one, so
     * the only way back is a new session.
     */
    failedMcpServers: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
    /**
     * The user whose inference credential the session's process was spawned
     * with. The process reads the credential once, at spawn, so this is whose
     * key every request of the session presents.
     */
    credentialUserId: Schema.optional(TrimmedNonEmptyString),
});
export const ProviderSessionStartInput = Schema.Struct({
    threadId: ThreadId,
    provider: Schema.optional(ProviderDriverKind),
    // See ProviderSession for the migration story.
    providerInstanceId: Schema.optional(ProviderInstanceId),
    cwd: Schema.optional(TrimmedNonEmptyString),
    modelSelection: Schema.optional(ModelSelection),
    resumeCursor: Schema.optional(Schema.Unknown),
    /** Whose inference credential the process is spawned with; absent spawns it with none. */
    credentialUserId: Schema.optional(TrimmedNonEmptyString),
});
export const ProviderSendTurnInput = Schema.Struct({
    threadId: ThreadId,
    input: Schema.optional(TrimmedNonEmptyString.check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_INPUT_CHARS))),
    attachments: Schema.optional(Schema.Array(ChatAttachment).check(Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS))),
    modelSelection: Schema.optional(ModelSelection),
});
export const ProviderTurnStartResult = Schema.Struct({
    threadId: ThreadId,
    turnId: TurnId,
    resumeCursor: Schema.optional(Schema.Unknown),
});
export const ProviderInterruptTurnInput = Schema.Struct({
    threadId: ThreadId,
    turnId: Schema.optional(TurnId),
});
export const ProviderStopSessionInput = Schema.Struct({
    threadId: ThreadId,
});
const ProviderEventKind = Schema.Literals(["session", "notification", "error"]);
export const ProviderEvent = Schema.Struct({
    id: EventId,
    kind: ProviderEventKind,
    provider: ProviderDriverKind,
    // See ProviderSession for the migration story.
    providerInstanceId: Schema.optional(ProviderInstanceId),
    threadId: ThreadId,
    createdAt: IsoDateTime,
    method: TrimmedNonEmptyString,
    message: Schema.optional(TrimmedNonEmptyString),
    turnId: Schema.optional(TurnId),
    itemId: Schema.optional(ProviderItemId),
    textDelta: Schema.optional(Schema.String),
    payload: Schema.optional(Schema.Unknown),
});
